import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { INTERACTION_OUTCOMES } from "@/lib/rbac";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CUSTOM = "__custom";

// Small "Add response" prompt shown on an interaction until its outcome is filled.
// `patchUrl` is the PATCH endpoint for this interaction. Calls onSaved(outcome) after success.
export const OutcomeSelect = ({ patchUrl, onSaved, testid, compact }) => {
  const [custom, setCustom] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (outcome) => {
    if (!outcome || !outcome.trim()) return;
    setBusy(true);
    try {
      await api.patch(patchUrl, { outcome: outcome.trim() });
      toast.success("Response recorded");
      onSaved?.(outcome.trim());
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (custom)
    return (
      <div className="flex items-center gap-2" data-testid={testid ? `${testid}-custom` : undefined}>
        <Input
          autoFocus
          className={compact ? "h-8 w-44 text-xs" : "min-h-[40px]"}
          placeholder="Type response"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && save(text)}
        />
        <Button size="sm" disabled={busy} onClick={() => save(text)}>Save</Button>
      </div>
    );

  return (
    <Select
      onValueChange={(v) => (v === CUSTOM ? setCustom(true) : save(v))}
      disabled={busy}
    >
      <SelectTrigger
        className={compact ? "h-8 w-48 text-xs" : "min-h-[40px] w-full sm:w-64"}
        data-testid={testid || "outcome-select"}
      >
        <SelectValue placeholder="Add response…" />
      </SelectTrigger>
      <SelectContent>
        {INTERACTION_OUTCOMES.map((o) => (
          <SelectItem key={o} value={o}>{o}</SelectItem>
        ))}
        <SelectItem value={CUSTOM}>Custom…</SelectItem>
      </SelectContent>
    </Select>
  );
};
